import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import { statisticMessages } from 'src/features/statistic/utils/messages';
import DataTableDateDropdown from 'src/registry/components/table/DataTableDateDropdown';
import { Button } from 'src/registry/ui/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from 'src/registry/ui/DropdownMenu';
import { ICON_MAP } from 'src/utils/constants';

enum Period {
    TODAY = 'today',
    YESTERDAY = 'yesterday',
    CUSTOM = 'custom',
}

const DATE_FORMAT = 'YYYY-MM-DDTHH:mm';

const Dot = () => <div className="size-2.5 rounded-full bg-green-600" />;

const getDatetimeToday = () => {
    return {
        from_datetime: dayjs().set('hour', 0).set('minute', 0).format(DATE_FORMAT),
        to_datetime: dayjs().format(DATE_FORMAT),
    };
};

type DateParams = {
    from_datetime: string;
    to_datetime: string;
};
type Props = {
    setDateParams: (data: DateParams) => void;
    dateParams: DateParams;
};

export default function DurationPicker({ dateParams, setDateParams }: Props) {
    const { formatMessage } = useIntl();
    const [activePeriod, setActivePeriod] = React.useState(Period.TODAY);

    const handlePeriodChange = React.useCallback(
        (period: Period) => {
            if (period === Period.TODAY) {
                setDateParams(getDatetimeToday());
            } else if (period === Period.YESTERDAY) {
                const from_datetime = dayjs().subtract(1, 'day').set('hour', 0).set('minute', 0).format(DATE_FORMAT);
                const to_datetime = dayjs().subtract(1, 'day').set('hour', 23).set('minute', 59).format(DATE_FORMAT);
                setDateParams({ from_datetime, to_datetime });
            }
            setActivePeriod(period);
        },
        [setDateParams],
    );

    return (
        <div className="flex">
            <Button
                variant="ghost"
                className="rounded-none rounded-l-lg border"
                onClick={() => handlePeriodChange(Period.TODAY)}
            >
                {activePeriod === Period.TODAY && <Dot />}
                {formatMessage(statisticMessages.today)}
            </Button>
            <Button
                variant="ghost"
                className="rounded-none border-y"
                onClick={() => handlePeriodChange(Period.YESTERDAY)}
            >
                {activePeriod === Period.YESTERDAY && <Dot />}
                {formatMessage(statisticMessages.yesterday)}
            </Button>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="rounded-none rounded-r-lg border">
                        {activePeriod === Period.CUSTOM && <Dot />}
                        <FontAwesomeIcon icon={ICON_MAP.CALENDAR} />
                        {formatMessage(statisticMessages.custom)}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DataTableDateDropdown
                        className="px-0"
                        setValue={({ afterDate, beforeDate }) => {
                            setDateParams({
                                from_datetime: dayjs(afterDate.toISOString())
                                    .set('hour', 0)
                                    .set('minute', 0)
                                    .format(DATE_FORMAT),
                                to_datetime: dayjs(beforeDate.toISOString())
                                    .set('hour', 23)
                                    .set('minute', 59)
                                    .format(DATE_FORMAT),
                            });
                            handlePeriodChange(Period.CUSTOM);
                        }}
                        inline
                        afterDate={new Date(dateParams.from_datetime)}
                        beforeDate={new Date(dateParams.to_datetime)}
                    />
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
